import React, { useState, useEffect, JSX, useRef } from 'react';
import { Box, Button, useMediaQuery, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { styled, useTheme } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';

import { useAuth } from 'hooks/AuthContext';
import * as Types from 'types/types';
import { useAppState } from 'hooks/GlobalContext';
import { DocumentList } from './DocumentList';
import { DocumentContentViewer } from './DocumentContentViewer';
import { getDocumentTypeFromFilename } from './documentUtils';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface DocumentManagerProps {
  filter?: string;
  onDocumentSelect?: (document: Types.Document | null) => void;
  setFilter?: (filter: string) => void;
  filenames?: string[];
}

const DocumentManager = (props: DocumentManagerProps): JSX.Element => {
  const { filter = '', filenames = [], setFilter, onDocumentSelect } = props;
  const theme = useTheme();
  const { setSnack } = useAppState();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, apiClient } = useAuth();

  const [documents, setDocuments] = useState<Types.Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Types.Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Types.Document | null>(null);
  const [documentContent, setDocumentContent] = useState<string>('');
  const [isViewingContent, setIsViewingContent] = useState(false);

  // Check if user is a candidate
  const candidate = user?.userType === 'candidate' ? (user as Types.Candidate) : null;

  useEffect(() => {
    onDocumentSelect && onDocumentSelect(selectedDocument);
  }, [selectedDocument, onDocumentSelect]);

  const prevDepsRef = useRef({ documents, filenames });

  useEffect(() => {
    const prev = prevDepsRef.current;

    // Check if the actual content changed
    const shouldUpdate =
      documents.length !== prev.documents.length ||
      filenames.length !== prev.filenames.length ||
      documents.some((doc, i) => doc.filename !== prev.documents[i]?.filename) ||
      filenames.some((name, i) => name !== prev.filenames[i]);

    if (shouldUpdate) {
      prevDepsRef.current = { documents, filenames };

      if (filenames.length > 0) {
        const filtered = documents.filter(doc => filenames.includes(doc.filename));
        setFilteredDocuments(filtered);
      } else {
        setFilteredDocuments(documents);
      }
    }
  }, [documents, filenames]);

  // Load documents on component mount
  useEffect(() => {
    const loadDocuments = async (): Promise<void> => {
      try {
        const results = await apiClient.getCandidateDocuments();
        setDocuments(results.documents);
      } catch (error) {
        console.error(error);
        setSnack('Failed to load documents', 'error');
      }
    };

    if (candidate) {
      loadDocuments();
    }
  }, [candidate, apiClient, setSnack]);

  // Handle document upload
  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const docType = getDocumentTypeFromFilename(file.name);

      if (!docType) {
        setSnack('Invalid file type. Please upload .txt, .md, .docx, or .pdf files only.', 'error');
        return;
      }

      try {
        // Upload file (replace with actual API call)
        const controller = apiClient.uploadCandidateDocument(
          file,
          { includeInRag: true, isJobDocument: false },
          {
            onError: error => {
              console.error(error);
              setSnack(error.content, 'error');
            },
          }
        );
        const result = await controller.promise;
        if (result && result.document) {
          setDocuments(prev => [...prev, result.document]);
          setSnack(`Document uploaded: ${file.name}`, 'success');
        }

        // Reset file input
        e.target.value = '';
      } catch (error) {
        console.error(error);
        setSnack('Failed to upload document', 'error');
      }
    }
  };

  const updateDocuments = (updatedDocs: Types.Document[]): void => {
    // Find documents that were deleted (in filteredDocuments but not in updatedDocs)
    const deletedDocIds = filteredDocuments
      .filter(doc => !updatedDocs.some(updated => updated.id === doc.id))
      .map(doc => doc.id);

    // Update the main documents array:
    // 1. Remove any deleted documents
    // 2. Update any modified documents
    const updatedDocuments = documents
      .filter(doc => !deletedDocIds.includes(doc.id)) // Remove deleted docs
      .map(doc => {
        // Check if this document was modified in updatedDocs
        const modifiedDoc = updatedDocs.find(updated => updated.id === doc.id);
        return modifiedDoc || doc; // Use modified version if available, otherwise keep original
      });

    setDocuments(updatedDocuments);
    setFilteredDocuments(updatedDocs); // Update filtered docs to match what child returned
  };

  if (!candidate) {
    return <Box>You must be logged in as a candidate to view this content.</Box>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        position: 'relative',
        flexDirection: 'column',
        flexGrow: 1,
        gap: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          m: 0,
          p: 1,
          width: '100%',
          verticalAlign: 'center',
          gap: 1,
        }}
      >
        <Typography variant={isMobile ? 'subtitle2' : 'h6'}>Documents</Typography>
        {filter && (
          <>
            <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
              RAG filter: {filter}
            </Typography>
            <ClearIcon
              sx={{
                height: 16,
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
              onClick={() => {
                setFilter && setFilter('');
              }}
            />
          </>
        )}
        <Button
          component="label"
          variant="contained"
          startIcon={<CloudUpload />}
          size={isMobile ? 'small' : 'medium'}
          sx={{ justifySelf: 'flex-end', ml: 'auto' }}
        >
          Upload Document
          <VisuallyHiddenInput
            type="file"
            accept=".txt,.md,.docx,.pdf"
            onChange={handleDocumentUpload}
          />
        </Button>
      </Box>

      <Box>
        {documents.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              textAlign: 'center',
              py: 3,
            }}
          >
            No additional documents uploaded
          </Typography>
        ) : (
          <DocumentList
            {...{
              documents: filteredDocuments,
              setDocuments: updateDocuments,
              selectedDocument,
              setSelectedDocument,
            }}
          />
        )}
      </Box>

      {/* Document Content Viewer */}
      {isViewingContent && (
        <DocumentContentViewer
          content={documentContent}
          isMobile={isMobile}
          onClose={(): void => {
            setIsViewingContent(false);
            setSelectedDocument(null);
            setDocumentContent('');
          }}
        />
      )}
    </Box>
  );
};

export { DocumentManager };
